import { Controller } from "@hotwired/stimulus"
import React, { Suspense } from "react"
import { render, unmountComponentAtNode } from "react-dom"
import {
  ErrorBoundary,
  Loading,
  ModalContainer,
  ObjectTable,
} from "web/components"
import { BrowserRouter as Router } from "react-router-dom"
import { NavigationProvider } from "web/contexts"
import { AppProviders, LibraryProviders } from "web/Providers"

export default class ObjectTableController extends Controller {
  static values = {
    viewGroup: Object,
    viewId: Number,
    subjectType: String,
    itemClickBaseUrl: String,
    subjectBaseUrl: String,
    addItemUrl: String,
    parentSubjectType: String,
    parentSubjectId: Number,
    enableScheduledExport: Boolean,
    enableCreation: { default: false, type: Boolean },
    enableEdit: { default: true, type: Boolean },
    enableSelection: { default: false, type: Boolean },
    enableExport: { default: true, type: Boolean },
    enableFieldSelection: { default: true, type: Boolean },
    exportPath: String,
    isFavouriteEnabled: { default: true, type: Boolean },
    fetchUrl: String,
    listTypeFriendlyName: String,
    topic: Object,
    modalTitle: String,
  }

  initialize() {
    global.React = React
  }

  onItemClick(item) {
    window.Turbo.visit(`${this.itemClickBaseUrlValue}/${item.data.id}`)
  }

  onAdd() {
    window.Turbo.visit(this.addItemUrlValue, { frame: "modal" })
  }

  disconnect() {
    // Turbo transplants data-turbo-permanent elements during page renders,
    // which briefly disconnects them; only unmount when the element truly
    // left the page.
    requestAnimationFrame(() => {
      if (!this.element.isConnected) {
        unmountComponentAtNode(this.element)
      }
    })
  }

  connect() {
    const parentSubjectType = this.hasParentSubjectTypeValue
      ? this.parentSubjectTypeValue
      : undefined

    const parentSubjectId = this.hasParentSubjectIdValue
      ? this.parentSubjectIdValue
      : undefined

    render(
      <Suspense fallback={<Loading inline />}>
        <Router>
          <LibraryProviders>
            <AppProviders>
              <NavigationProvider
                viewGroup={this.viewGroupValue}
                viewId={this.viewIdValue}
                subjectType={this.subjectTypeValue}
              >
                <ErrorBoundary>
                  <ObjectTable
                    subjectType={this.subjectTypeValue}
                    topic={this.hasTopicValue ? this.topicValue : undefined}
                    modalTitle={this.modalTitleValue}
                    enableEdit={this.enableEditValue}
                    enableExport={this.enableExportValue}
                    enableFieldSelection={this.enableFieldSelectionValue}
                    exportPath={this.exportPathValue}
                    onItemClick={
                      this.hasItemClickBaseUrlValue
                        ? this.onItemClick.bind(this)
                        : undefined
                    }
                    onAdd={this.enableCreationValue && this.onAdd.bind(this)}
                    subjectBaseUrl={
                      this.hasSubjectBaseUrlValue
                        ? this.subjectBaseUrlValue
                        : undefined
                    }
                    parentSubjectType={parentSubjectType}
                    parentSubjectId={parentSubjectId}
                    enableScheduledExport={this.enableScheduledExportValue}
                    isFavouriteEnabled={this.isFavouriteEnabledValue}
                    fetchURL={this.fetchUrlValue}
                    listTypeFriendlyName={this.listTypeFriendlyNameValue}
                    enableSelection={this.enableSelectionValue}
                  />
                </ErrorBoundary>
              </NavigationProvider>
              <ModalContainer />
            </AppProviders>
          </LibraryProviders>
        </Router>
      </Suspense>,
      this.element
    )
  }
}
